import {useState} from "react"
import {
	Button,
	FormControl,
	FormControlLabel,
	Checkbox,
	InputLabel,
	Link,
	OutlinedInput,
	Typography,
} from "@mui/material"
import Grid from "@mui/material/Grid"
import {styled} from "@mui/material/styles"
import {useNotification} from "@indebted/components/Notification"
import {DiscountMessage} from "@indebted/components/DiscountMessage"
import {TextToHTML} from "@indebted/components/TextToHTML"
import {customerFind} from "@indebted/api/customer/customerFind"
import {customerAPI} from "@indebted/api/customerAPI"
import {useParams} from "react-router-dom"
import {DebitCardIcon} from "@indebted/assets"

const FormContent = ({
	onSubmit,
	i18n,
	discountMessage,
	name,
	email,
	paymentMethodSelectionLink,
	requestESIGNConsent,
	esignConsent,
}) => {
	const [form, setForm] = useState({
		Name: name,
		Email: email,
		ESIGNConsent: false,
	})
	const {secureCode} = useParams()
	const {notification} = useNotification()

	const [showESIGNErrorMessage, setShowESIGNErrorMessage] = useState(false)
	const [esignConsentEnabled, setESIGNConsentEnabled] = useState(true)

	function handleInput(name) {
		return ({target: {value}}) => {
			setForm({...form, [name]: value})
		}
	}

	async function submit(e) {
		e.preventDefault()

		if (requestESIGNConsent && !form.ESIGNConsent) {
			setShowESIGNErrorMessage(true)
			return
		}

		try {
			if (requestESIGNConsent && esignConsentEnabled) {
				const customer = await customerFind({secureCode})
				await customerAPI.grantESIGNConsent({customerID: customer.ID})
				setESIGNConsentEnabled(false)
			}
			onSubmit(form)
		} catch (error) {
			notification.error(error.message || error.Message, 5000)
		}
	}

	const onChangeESIGNConsentCheckbox = ({target: {checked}}) => {
		setForm({...form, ["ESIGNConsent"]: checked})
		setShowESIGNErrorMessage(!checked)
	}

	return (
		<form onSubmit={submit}>
			<Grid container direction="column" alignItems="stretch" spacing={5}>
				<Grid>
					<DiscountMessage discountMessage={discountMessage} />
				</Grid>
				<Grid>
					<Typography variant="h6">{i18n.Header}</Typography>
				</Grid>
				<Grid>
					<TextToHTML text={i18n.Title} />
				</Grid>
				<Grid>
					<FormControl variant="outlined" fullWidth={true}>
						<InputLabel htmlFor="accountHolderName">{i18n.FormFullNameLabel}</InputLabel>
						<OutlinedInput
							id="accountHolderName"
							type="text"
							label={i18n.FormFullNameLabel}
							value={form.Name}
							onChange={handleInput("Name")}
							required
							autoFocus
						/>
					</FormControl>
				</Grid>
				<Grid>
					<FormControl variant="outlined" fullWidth={true}>
						<InputLabel htmlFor="accountHolderEmail">{i18n.FormEmailLabel}</InputLabel>
						<OutlinedInput
							id="accountHolderEmail"
							type="email"
							label={i18n.FormEmailLabel}
							value={form.Email}
							onChange={handleInput("Email")}
							required
						/>
					</FormControl>
				</Grid>
				{requestESIGNConsent && (
					<Grid>
						<Grid container direction="column" alignItems="stretch" spacing={5} padding={0}>
							<Grid>
								<StyledFormControlLabel
									control={
										<StyledCheckbox
											disabled={!esignConsentEnabled}
											onChange={onChangeESIGNConsentCheckbox}
											name="esign-consent"
											color="primary"
											error={showESIGNErrorMessage}
										/>
									}
									label={
										<>
											{esignConsent.CheckboxText} {esignConsent.SeeOurTermsAndConditionsText}
											<Link
												href={esignConsent.TermsAndConditionsURL}
												rel="noopener noreferrer"
												target="_blank"
											>
												{esignConsent.TermsAndConditionsLabel}
											</Link>
										</>
									}
								/>
							</Grid>
						</Grid>
					</Grid>
				)}
				{showESIGNErrorMessage && (
					<Grid>
						<StyledAlertGrid>
							<TextToHTML text={esignConsent.ErrorMessage} />
						</StyledAlertGrid>
					</Grid>
				)}
				<Grid>
					<Button fullWidth type="submit">
						{i18n.FormButtonLabel}
					</Button>
				</Grid>
				<Grid style={{paddingTop: "3px", paddingRight: "5px", display: "flex", alignItems: "flex-start"}}>
					<DebitCardIcon />
					<Typography style={{marginLeft: "5px"}}>
						{i18n.FormPaymentMethodSelectionText}
						<Link href={paymentMethodSelectionLink} rel="noopener noreferrer">
							{i18n.FormPaymentMethodSelectionLinkText}
						</Link>
					</Typography>
				</Grid>
			</Grid>
		</form>
	)
}

const StyledFormControlLabel = styled(FormControlLabel)`
	align-items: start;
`

const StyledCheckbox = styled(Checkbox)<{error: boolean}>`
	padding-top: 0;

	svg {
		color: ${(props) => (props.error ? "#D7153A" : "inherit")};
	}
`
const StyledAlertGrid = styled(Grid)`
	border: solid 1px #d7153a;
	border-radius: 5px;
	margin-bottom: 10px;
	padding: 10px;

	p {
		font-size: 0.75rem;
		color: #d7153a;
	}
`

export {FormContent}
